import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import JSZip from "jszip";
import { OrderDocument, OrderDocumentField } from "@/models";
import { associationApi } from "@/services/api";
import { orderApplicantApi } from "@/services/api/orderApplicantApi";
import { normalizeOrderDocumentFields } from "@/utils/orderDocumentFields";
import {
  resolveStorageImagesIn,
  resolveStorageUrl,
  storageFileExtension,
} from "@/utils/storageRefs";

// By extension, ignoring any query string: a signed link ends in ?token=….
const isImageUrl = (url: string): boolean =>
  ["jpg", "jpeg", "png", "gif", "webp"].includes(storageFileExtension(url));

const isPdfUrl = (url: string): boolean => storageFileExtension(url) === "pdf";

const formatDateForPdf = (fieldName: string, value: any): string => {
  if (!value) return "N/A";

  if (fieldName.toLowerCase().includes("date")) {
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString();
      }
    } catch (error) {
      console.error("Error formatting date for PDF:", error);
    }
  }
  return String(value);
};

const loadPdf = async (value: string): Promise<ArrayBuffer> => {
  // The stored value names a private file; fetch it through a signed link.
  const url = (await resolveStorageUrl(value)) ?? value;
  try {
    const response = await fetch(url, {
      mode: "cors",
      credentials: "omit",
      headers: {
        Accept: "application/pdf",
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch PDF: ${response.status} ${response.statusText}`
      );
    }

    return await response.arrayBuffer();
  } catch (error) {
    console.error(`Error loading PDF from ${value}:`, error);
    throw error;
  }
};

const createAttachmentsZip = async (
  fileUrls: string[],
  mainPdfBlob: Blob
): Promise<Blob> => {
  const zip = new JSZip();

  zip.file("document.pdf", mainPdfBlob);

  for (let i = 0; i < fileUrls.length; i++) {
    try {
      const pdfArrayBuffer = await loadPdf(fileUrls[i]);
      // Extract the file extension from the URL
      let extension = "";
      try {
        const ext = storageFileExtension(fileUrls[i]);
        extension = ext ? `.${ext}` : "";
      } catch {
        // fallback if URL parsing fails
        const match = fileUrls[i].match(/\.([a-zA-Z0-9]+)$/);
        extension = match ? match[0] : "";
      }
      // Default to .bin if no extension found
      if (!extension) extension = ".bin";
      const fileName = `attachment_${i + 1}${extension}`;
      zip.file(fileName, pdfArrayBuffer);
    } catch (error) {
      console.error(`Error adding attachment to zip: ${fileUrls[i]}`, error);
    }
  }

  return await zip.generateAsync({ type: "blob" });
};

// Get section fields by section_id
const getSectionFieldValues = (
  sectionId: number | null,
  document: OrderDocument
) => {
  if (!document.fields || sectionId === null) return [];
  return document.fields.filter((f) => f.section_id === sectionId);
};
export const generateDocumentPdf = async (
  orderDocumentRaw: OrderDocument,
  associationName: string,
  orderId: number,
  orderDate: string,
  propertyAddress: string,
  ipAddress?: string | null,
  formData?: any
): Promise<Blob> => {
  // Normalize fields shape (see src/utils/orderDocumentFields.ts) so rows
  // written after 2026-03-24 (nested section-block shape) render the same as
  // older rows (flat shape).
  const orderDocument: OrderDocument = {
    ...orderDocumentRaw,
    fields: normalizeOrderDocumentFields(orderDocumentRaw?.fields as any[]),
  };
  // --- Applicant roster, for attributing signatures to signers (D14) -------
  // Signature values carry applicant_uid; legacy single-signer values do not,
  // in which case this stays empty and the PDF renders exactly as before.
  const applicantByUid: Record<string, { name: string; index: number }> = {};
  try {
    const roster = await orderApplicantApi.list(orderId);
    for (const r of roster || []) {
      if (!r?.applicant_uid) continue;
      applicantByUid[r.applicant_uid] = {
        name:
          r.name ||
          [r.first_name, r.last_name].filter(Boolean).join(" ") ||
          `Applicant ${(r.applicant_index ?? 0) + 1}`,
        index: r.applicant_index ?? 0,
      };
    }
  } catch (e) {
    // Non-fatal: signatures simply render without a signer label.
    console.warn("Could not load applicant roster for PDF:", e);
  }

  /**
   * Label for a signature that carries no applicant_uid.
   *
   * Signatures captured during normal form filling go through the ordinary
   * order PATCH, which knows nothing about applicants, so they have no uid.
   * They belong to the person who filled the form — applicant 1 — so name them
   * rather than leaving them unattributed. Returns null when there is no roster
   * (legacy single-signer orders), leaving those PDFs unchanged.
   */
  const soleSignerLabel = (field: { type?: string }): string | null => {
    if (field?.type !== "signature" && field?.type !== "initials") return null;
    const main = Object.values(applicantByUid).find((a) => a.index === 0);
    return main ? `Applicant 1 — ${main.name}` : null;
  };

  // --- Association Name Resolution (match DocumentDetail.tsx) ---
  let resolvedAssociationName: string | null = null;
  try {
    const assoc = await associationApi.getById(orderDocument.association_id);
    resolvedAssociationName = assoc?.name || null;
  } catch (e) {
    resolvedAssociationName = null;
  }
  if (!resolvedAssociationName) {
    // Fallback to prop if provided, otherwise show ID
    resolvedAssociationName = associationName || `Association ID: ${orderDocument.association_id}`;
  }
  // --- END Association Name Resolution ---
  // Initialize pdf before using pageWidth and safeMarginMM
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
    hotfixes: ["px_scaling"],
  });

  // Define safeMarginMM after pdf is initialized
  const safeMarginMM = 5; // Reduced margin

  // Create an offscreen container for PDF generation
  const tempDiv = document.createElement("div");
  tempDiv.style.position = "absolute";
  tempDiv.style.left = "-9999px";
  tempDiv.style.width = "800px";
  document.body.appendChild(tempDiv);

  // Use fileUrls instead of pdfUrls
  const fileUrls: string[] = [];
  const fileUrlSet = new Set<string>(); // Prevent duplicates

  console.log("PDF Generation - Document fields:", orderDocument);
  console.log(
    "PDF Generation - Document updated_at:",
    orderDocument.updated_at
  );
  console.log("PDF Generation - Form data:", formData);

  // Constants for PDF layout
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const imgWidth = pageWidth - 2 * safeMarginMM;
  const maxContentHeight = pageHeight - 2 * safeMarginMM - 15;

  let currentPageHeight = 0;
  let isFirstPage = true;

  // Helper method to generate field HTML
  const generateFieldHtml = (
    field: any,
    fieldValue: any,
    documentUpdatedAt: string,
    // Signer attribution for signature/initials (D14). Omitted on legacy
    // single-signer orders, so their PDFs render exactly as before.
    applicantLabel?: string | null
  ): string => {
    if (
      (field.type === "file" ||
        field.type === "signature" ||
        field.type === "initials") &&
      fieldValue
    ) {
      // Always add file URLs to attachments, regardless of type
      if (field.type === "file" && !fileUrlSet.has(fieldValue)) {
        fileUrls.push(fieldValue);
        fileUrlSet.add(fieldValue);
      }
      if (isImageUrl(fieldValue)) {
        const signerHtml =
          applicantLabel &&
          (field.type === "signature" || field.type === "initials")
            ? `<p style="font-size: 12px; font-weight: 600; color: #444; margin: 0 0 4px 0;">${applicantLabel}</p>`
            : "";

        let content = `
          <div style="margin-bottom: 12px;">
            <label style="font-size: 14px; font-weight: 500; color: #666; display: block; margin-bottom: 6px;">${field.label}</label>
            ${signerHtml}
            <div style="min-height: 50px;">
              <img src="${fieldValue}" style="max-width: 400px; max-height: 300px; width: auto; height: auto; display: block;" />`;

        // Use the document's updated_at timestamp for all signature/initials fields
        if (
          (field.type === "signature" || field.type === "initials") &&
          documentUpdatedAt
        ) {
          const formattedDate = new Date(documentUpdatedAt).toLocaleString();
          content += `
              <p style="font-size: 12px; color: #555; margin-top: 3px; margin-bottom: 0;">${formattedDate}</p>`;
        }

        content += `
            </div>
          </div>`;
        return content;
      } else if (isPdfUrl(fieldValue)) {
        // Already added to fileUrls above
        return `
          <div style="margin-bottom: 12px;">
            <label style="font-size: 14px; font-weight: 500; color: #666; display: block; margin-bottom: 6px;">${field.label}</label>
            <div style="padding: 8px; background-color: #f5f5f5; border-radius: 4px;">
              <p style="font-size: 14px; margin: 0;">PDF Attachment: Will be included in attachments.zip</p>
            </div>
          </div>`;
      } else {
        // For other file types, just show a generic attachment message
        return `
          <div style="margin-bottom: 12px;">
            <label style="font-size: 14px; font-weight: 500; color: #666; display: block; margin-bottom: 6px;">${field.label}</label>
            <div style="padding: 8px; background-color: #f5f5f5; border-radius: 4px;">
              <p style="font-size: 14px; margin: 0;">File Attachment: Will be included in attachments.zip</p>
            </div>
          </div>`;
      }
    } else {
      // Render regular field
      return `
        <div style="margin-bottom: 12px;">
          <label style="font-size: 14px; font-weight: 500; color: #666; display: block; margin-bottom: 6px;">${
            field.label
          }</label>
          <p style="font-size: 16px; min-height: 20px; margin: 0;">${formatDateForPdf(
            field.name || field.label,
            fieldValue
          )}</p>
        </div>`;
    }
    return "";
  };

  // Helper function to add content block to PDF
  const addContentBlockToPdf = async (
    htmlContent: string,
    forceNewPage = false
  ) => {
    // Create a temporary container for this block
    const blockDiv = document.createElement("div");
    blockDiv.style.position = "absolute";
    blockDiv.style.left = "-9999px";
    blockDiv.style.width = "800px";
    blockDiv.style.background = "white";
    blockDiv.style.fontFamily = "Arial, sans-serif";
    blockDiv.style.padding = "8px";
    blockDiv.innerHTML = htmlContent;
    document.body.appendChild(blockDiv);

    try {
      // Signatures and image uploads are private files: swap their stored
      // refs for signed links before waiting on them.
      await resolveStorageImagesIn(blockDiv);

      // Wait for images to load
      await Promise.all(
        Array.from(blockDiv.querySelectorAll("img")).map((img) => {
          return new Promise((resolve) => {
            if (img.complete) {
              resolve(null);
            } else {
              img.onload = () => resolve(null);
              img.onerror = (error) => {
                console.warn("Failed to load image:", img.src, error);
                img.style.display = "none";
                resolve(null);
              };
            }
          });
        })
      );

      // Render the block to canvas
      const canvas = await html2canvas(blockDiv, {
        logging: false,
        useCORS: true,
        allowTaint: true,
        width: blockDiv.offsetWidth,
        height: blockDiv.offsetHeight,
      });

      const blockHeight = (canvas.height * imgWidth) / canvas.width;

      // Check if we need a new page
      if (
        forceNewPage ||
        (!isFirstPage && currentPageHeight + blockHeight > maxContentHeight)
      ) {
        pdf.addPage();
        currentPageHeight = 0;
      }

      // Add the block to the PDF
      pdf.addImage(
        canvas.toDataURL("image/jpeg", 0.98),
        "JPEG",
        safeMarginMM,
        safeMarginMM + currentPageHeight,
        imgWidth,
        blockHeight,
        undefined,
        "FAST"
      );

      currentPageHeight += blockHeight;
      isFirstPage = false;
    } finally {
      // Clean up
      document.body.removeChild(blockDiv);
    }
  };

  // Create header content
  let headerContent = `
    <div style="padding: 8px; background: white; width: 800px;">
      <div style="display: flex; justify-content: center; margin-bottom: 8px;">
        <img src="/lovable-uploads/753f375c-83b4-4a92-a62f-7aa150906582.png" style="height: 50px;" />
      </div>`;

  // Add association name header if it exists
  if (resolvedAssociationName) {
    headerContent += `
      <div style="background: linear-gradient(90deg, #FF6600 0%, #FF8C44 100%); color: white; text-align: center; padding: 12px; 
        border-radius: 8px; margin-bottom: 10px; font-weight: bold; font-size: 16px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); display: flex; align-items: center; justify-content: center; min-height: 48px;">
        <span style="display: inline-block; vertical-align: middle;">${resolvedAssociationName}</span>
      </div>`;
  }

  headerContent += `
      <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 15px; margin-bottom: 12px;">
        <div>
          <h1 style="font-weight: bold; margin-bottom: 6px; font-size: 16px; color: #FF6600;">${
            orderDocument.name || `Document #${orderDocument.document_id}`
          }</h1>
          <p style="color: #666; margin: 0;">Order #${orderId}</p>
          ${
            ipAddress
              ? `<p style="color: #666; font-size: 12px; margin: 0;">IP: ${ipAddress}</p>`
              : ""
          }
        </div>
        
        <div style="text-align: center;">
          <p style="color: #666; margin: 0;">${propertyAddress}</p>
        </div>
        
        <div style="text-align: right;">
          <p style="color: #666; margin-bottom: 3px;">Issuance Date</p>
          <p style="font-size: 18px; margin: 0;">${new Date(
            orderDate
          ).toLocaleDateString()}</p>
        </div>
      </div>
    </div>`;

  // Add header
  await addContentBlockToPdf(headerContent);

  if (orderDocument.sections && Array.isArray(orderDocument.sections)) {
    // Create a map of section IDs to their form step order and section order from form data
    const sectionOrderMap = new Map();

    if (formData?.form_steps && Array.isArray(formData.form_steps)) {
      formData.form_steps.forEach((step, stepIndex) => {
        if (step.form_sections && Array.isArray(step.form_sections)) {
          step.form_sections.forEach((section) => {
            sectionOrderMap.set(section.id, {
              step_order: step.step_order || stepIndex,
              section_order: section.section_order || 0,
              title: section.section_title || section.title,
            });
          });
        }
      });
    }

    // Use sections in the order they are already provided (pre-sorted from RequestedDocumentsTab.tsx)
    const sortedSections = [...orderDocument.sections];

    // Filter out sections that have no fields to display
    const sectionsWithContent = sortedSections.filter((section) => {
      // Check if this section has any fields in the document
      if (orderDocument.fields && Array.isArray(orderDocument.fields)) {
        const sectionHasFields = orderDocument.fields.some(
          (f) => f.section_id === section.section_id
        );
        return sectionHasFields;
      }
      // If section already has fields array populated, check if it has content
      if (section.fields && Array.isArray(section.fields)) {
        return section.fields.length > 0;
      }
      return false;
    });

    for (const section of sectionsWithContent) {
      if (!section.fields || !Array.isArray(section.fields)) {
        section.fields = [];

        // Try to populate fields from document.fields if they exist
        if (orderDocument.fields && Array.isArray(orderDocument.fields)) {
          const sectionFields = orderDocument.fields.filter(
            (f) => f.section_id === section.section_id
          );
          if (sectionFields.length > 0) {
            // Create a map of field IDs to their field_order from form data
            const fieldOrderMap = new Map();

            if (formData?.form_steps) {
              for (const step of formData.form_steps) {
                if (step.form_sections) {
                  for (const formSection of step.form_sections) {
                    if (
                      formSection.id === section.section_id &&
                      formSection.form_fields
                    ) {
                      formSection.form_fields.forEach((field) => {
                        fieldOrderMap.set(field.id, {
                          field_order: field.field_order || 0,
                          label: field.label,
                          field_type: field.field_type,
                          name: field.name,
                        });
                      });
                    }
                  }
                }
              }
            }

            // Convert FormFieldValue objects to OrderDocumentField objects with labels from form data
            // Create unique field definitions (one per field_id) instead of duplicating for each index
            const uniqueFieldIds = [
              ...new Set(sectionFields.map((f) => f.field_id)),
            ];
            section.fields = uniqueFieldIds
              .map((fieldId) => {
                // Get the first instance of this field to use for field definition
                const fieldInstance = sectionFields.find(
                  (f) => f.field_id === fieldId
                );
                if (!fieldInstance) return null;

                // Try to find field information from form data
                const fieldInfo = fieldOrderMap.get(fieldId) || {};
                const fieldLabel = fieldInfo.label || `Field ${fieldId}`;
                const fieldType = fieldInfo.field_type || "text";
                const fieldName = fieldInfo.name || `field_${fieldId}`;

                return {
                  id: fieldId,
                  field_id: fieldId,
                  label: fieldLabel,
                  name: fieldName,
                  type: fieldType,
                  value: fieldInstance.value, // This will be overridden when getting values by index
                  index: fieldInstance.index, // This will be used for reference but overridden
                  section_id: fieldInstance.section_id,
                  field_order: fieldInfo.field_order || 0,
                } as OrderDocumentField;
              })
              .filter(Boolean) as OrderDocumentField[];

            // Sort fields by field_order
            section.fields.sort((a, b) => {
              const orderA = a.field_order || 0;
              const orderB = b.field_order || 0;
              return orderA - orderB;
            });
          }
        }
      } else {
        // If fields are already present, sort them by field_order
        // Create a map of field IDs to their field_order from form data
        const fieldOrderMap = new Map();

        if (formData?.form_steps) {
          for (const step of formData.form_steps) {
            if (step.form_sections) {
              for (const formSection of step.form_sections) {
                if (
                  formSection.id === section.section_id &&
                  formSection.form_fields
                ) {
                  formSection.form_fields.forEach((field) => {
                    fieldOrderMap.set(field.id, {
                      field_order: field.field_order || 0,
                    });
                  });
                }
              }
            }
          }
        }

        // Apply field_order to existing fields
        section.fields.forEach((field) => {
          // Use the optional chaining operator to safely access properties
          const fieldId = field.id || field.field_id;
          const fieldInfo = fieldOrderMap.get(fieldId);
          if (fieldInfo) {
            field.field_order = fieldInfo.field_order;
          } else {
            field.field_order = 0;
          }
        });

        // Sort by field_order
        section.fields.sort((a, b) => {
          const orderA = a.field_order || 0;
          const orderB = b.field_order || 0;
          return orderA - orderB;
        });
      }

      const visibleFields = section.fields.filter(
        (field) => field.type !== "information"
      );

      if (visibleFields.length > 0) {
        // Find the section_id from the first field
        const sectionId = visibleFields[0]?.section_id || null;

        // Get all field values for this section
        const sectionFields = getSectionFieldValues(sectionId, orderDocument);

        // Get unique indices from the document.fields for this section
        const uniqueIndices = [
          ...new Set(sectionFields.map((f) => f.index || 0)),
        ].sort();

        // Try to get section title from form data
        let sectionTitle = section.title;
        const sectionInfo = sectionOrderMap.get(section.section_id);
        if (sectionInfo?.title) {
          sectionTitle = sectionInfo.title;
        } else if (formData?.form_steps && sectionId) {
          for (const step of formData.form_steps) {
            if (step.form_sections) {
              const formSection = step.form_sections.find(
                (s: any) => s.id === sectionId
              );
              if (formSection?.section_title) {
                sectionTitle = formSection.section_title;
                break;
              }
            }
          }
        }

        // Add section header
        const sectionHeaderContent = `
          <div style="padding: 8px 8px 0px 8px; background: white; width: 800px;">
            <h3 style="font-size: 18px; font-weight: 600; border-bottom: 1px solid #ddd; padding-bottom: 0px; margin-bottom: 0px; margin-top: 12px;">${sectionTitle}</h3>
          </div>`;

        await addContentBlockToPdf(sectionHeaderContent);

        // Process each entry index
        for (const index of uniqueIndices) {
          // Only add Entry header if there are multiple entries
          if (uniqueIndices.length > 1) {
            const entryHeaderContent = `
              <div style="padding: 8px; background: white; width: 800px;">
                <div style="font-size: 16px; font-weight: 500; color: #374151; margin-bottom: 2px; margin-top: 2px;">
                  Entry ${index + 1}
                </div>
              </div>`;
            await addContentBlockToPdf(entryHeaderContent);
          }

          // Get fields for this specific index
          const fieldsForIndex = visibleFields.filter((field) => {
            const matchingField = sectionFields.find(
              (f) => f.field_id === field.id && (f.index || 0) === index
            );
            return matchingField !== undefined;
          });

          // ---- Multi-signer signature/initials (D14) ----------------------
          // A fanned-out signature has one stored value PER SIGNER, all sharing
          // the same (field_id, index). The pair loop below matches on
          // (field_id, index) only, so it would render just the first signer.
          // Emit those fields here, one full-width block per signer, then
          // exclude them from the pair loop.
          const multiSignerFieldIds = new Set<number>();

          for (const f of fieldsForIndex) {
            if (f.type !== "signature" && f.type !== "initials") continue;

            // Every signed value for this slot, INCLUDING the main applicant's.
            //
            // Requiring sf.applicant_uid here dropped the main applicant: they
            // sign inline while filling the form, so their values carry no uid
            // (only invited co-applicants get one). On order 1676 doc 9085 all
            // ten values are stored — five with a uid, five without — but the
            // filter kept only the five with one, so the PDF showed a single
            // signature per field and the main applicant's was missing.
            const signerValues = sectionFields.filter(
              (sf) =>
                sf.field_id === f.id &&
                (sf.index || 0) === index &&
                String(sf.value ?? "") !== ""
            );

            // Only take over when there are genuinely multiple signers;
            // single-signer/legacy fields keep the existing code path so their
            // PDFs are byte-identical to today.
            if (signerValues.length < 2) continue;
            multiSignerFieldIds.add(f.id);

            const sorted = [...signerValues].sort((a, b) => {
              const ai = applicantByUid[a.applicant_uid as string]?.index ?? 0;
              const bi = applicantByUid[b.applicant_uid as string]?.index ?? 0;
              return ai - bi;
            });

            for (const sv of sorted) {
              const applicant = applicantByUid[sv.applicant_uid as string];
              // No uid means the main applicant signed inline while filling the
              // form; soleSignerLabel names them instead of leaving the block
              // unattributed next to a co-applicant's named one.
              const label = applicant
                ? `Applicant ${applicant.index + 1} — ${applicant.name}`
                : soleSignerLabel(f);

              const block = `
                <div style="padding: 0px 8px 4px 8px; background: white; width: 800px;">
                  <div style="display: grid; grid-template-columns: 1fr; gap: 12px;">
                    ${generateFieldHtml(
                      f,
                      sv.value,
                      orderDocument.updated_at,
                      label
                    )}
                  </div>
                </div>`;

              await addContentBlockToPdf(block);
            }
          }

          const pairableFields = fieldsForIndex.filter(
            (f: any) => !multiSignerFieldIds.has(f.id)
          );

          // Process fields in pairs for grid layout, but render each field individually to prevent splitting
          for (let i = 0; i < pairableFields.length; i += 2) {
            const field1 = pairableFields[i];
            const field2 = pairableFields[i + 1];

            // Find the matching field values
            const fieldValue1 =
              sectionFields.find(
                (f) => f.field_id === field1.id && (f.index || 0) === index
              )?.value || field1.value;

            const fieldValue2 = field2
              ? sectionFields.find(
                  (f) => f.field_id === field2.id && (f.index || 0) === index
                )?.value || field2.value
              : null;

            // Create grid content for up to 2 fields
            let gridContent = `
              <div style="padding: 0px 8px 4px 8px; background: white; width: 800px;">
                <div style="display: grid; grid-template-columns: 1fr${
                  field2 ? " 1fr" : ""
                }; gap: 12px;">`;

            // Add first field
            gridContent += generateFieldHtml(
              field1,
              fieldValue1,
              orderDocument.updated_at,
              soleSignerLabel(field1)
            );

            // Add second field if it exists
            if (field2) {
              gridContent += generateFieldHtml(
                field2,
                fieldValue2,
                orderDocument.updated_at,
                soleSignerLabel(field2)
              );
            }

            gridContent += `
                </div>
              </div>`;

            // Check if this content has images - if so, render each field separately
            const hasImages =
              (field1.type === "file" ||
                field1.type === "signature" ||
                field1.type === "initials") &&
              fieldValue1 &&
              isImageUrl(fieldValue1);
            const hasImages2 =
              field2 &&
              (field2.type === "file" ||
                field2.type === "signature" ||
                field2.type === "initials") &&
              fieldValue2 &&
              isImageUrl(fieldValue2);

            if (hasImages || hasImages2) {
              // Render fields with images separately to prevent splitting
              const field1Content = `
                <div style="padding: 0px 8px 4px 8px; background: white; width: 800px;">
                  <div style="display: grid; grid-template-columns: 1fr; gap: 12px;">
                    ${generateFieldHtml(
                      field1,
                      fieldValue1,
                      orderDocument.updated_at,
                      soleSignerLabel(field1)
                    )}
                  </div>
                </div>`;

              await addContentBlockToPdf(field1Content);

              if (field2) {
                const field2Content = `
                  <div style="padding: 0px 8px 4px 8px; background: white; width: 800px;">
                    <div style="display: grid; grid-template-columns: 1fr; gap: 12px;">
                      ${generateFieldHtml(
                        field2,
                        fieldValue2,
                        orderDocument.updated_at,
                        soleSignerLabel(field2)
                      )}
                    </div>
                  </div>`;

                await addContentBlockToPdf(field2Content);
              }
            } else {
              // No images, safe to render together
              await addContentBlockToPdf(gridContent);
            }
          }
        }
      }
    }
  }

  // Clean up
  document.body.removeChild(tempDiv);

  // Always add the attachment page summary as the last page
  pdf.addPage();
  pdf.setFontSize(16);
  pdf.text("Document Package Information", 20, 20);

  if (fileUrls.length > 0) {
    pdf.text(
      "This package includes the main document and all attachments.",
      20,
      30
    );
    pdf.text(`Number of attachments: ${fileUrls.length}`, 20, 40);
  } else {
    pdf.text("This package includes only the main document.", 20, 30);
    pdf.text("No attachments are included.", 20, 40);
  }

  const mainPdfBlob = pdf.output("blob");

  const zipBlob = await createAttachmentsZip(fileUrls, mainPdfBlob);

  return zipBlob;
};
